import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from '../../../componentes/contenedores/Card';
import GraficoBarra from '../../../componentes/graficos/Barras';
import GraficoLineal from '../../../componentes/graficos/Lineal2';
import FiltroFechaUI from "../../../componentes/metodos/FiltroFechaUI";
import useFechaFiltro from "../../../componentes/metodos/LogicaFiltro";
import Page from '../../../componentes/plantilla/Page';
import {
    AnalisisComparacionStock,
    AnalisisRentabilidadProductos,
    AnalisisResumenKPIProducto,
    AnalisisRotacionProductos,
    AnalisisTopProductos,
    AnalisisTopProductosMenosVendidos
} from '../../../configuracion/apiUrls';
import { formatoMoneda } from "../../../configuracion/validaciones";
moment.locale("es");

const ProductosAnalisis = () => {
    const [cargando, setCargando] = useState(true);
    const [chartRankingProductos, setChartRankingProductos] = useState(null);
    const [chartMenosVendidos, setChartMenosVendidos] = useState(null);
    const [chartRentabilidad, setChartRentabilidad] = useState(null);
    const [metricas, setMetricas] = useState({});
    const [chartStock, setChartStock] = useState(null);
    const [chartRotacion, setChartRotacion] = useState(null);

    const {
        filtro, setFiltro, fechaSeleccionada, setFechaSeleccionada,
        horaSeleccionada, setHoraSeleccionada, semanaSeleccionada, setSemanaSeleccionada,
        mesSeleccionado, setMesSeleccionado, anioSeleccionado, setAnioSeleccionado,
        obtenerFechasDeFiltro, maxAnio,
    } = useFechaFiltro();

    const cargarTodo = useCallback(async () => {
        setCargando(true);
        const fechas = obtenerFechasDeFiltro();
        const params = {
            fechaInicio: moment(fechas.fechaInicio).format('YYYY-MM-DD HH:mm:ss'),
            fechaFin: moment(fechas.fechaFin).format('YYYY-MM-DD HH:mm:ss'),
        };

        try {
            const [resProds, resRentabilidad, resMenos, resResumen, resStock, resRotacion] = await Promise.all([
                AxiosPublico.get(AnalisisTopProductos, { params }),
                AxiosPublico.get(AnalisisRentabilidadProductos, { params }),
                AxiosPublico.get(AnalisisTopProductosMenosVendidos, { params }),
                AxiosPublico.get(AnalisisResumenKPIProducto, { params }),
                AxiosPublico.get(AnalisisComparacionStock, { params }),
                AxiosPublico.get(AnalisisRotacionProductos, { params }),
            ]);

            // 1. Productos más Vendidos (Cantidad)
            if (resProds.data?.length > 0) {
                setChartRankingProductos({
                    labels: resProds.data.map(p => p.producto?.nombre || "N/A"),
                    data: resProds.data.map(p => parseFloat(p.cantidadVendida)),
                    colors: resProds.data.map((_, i) => `hsla(${180 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Productos más Vendidos"
                });
            } else { setChartRankingProductos(null); }

            // 2. Utilidad/Rentabilidad (Monetario)
            if (resRentabilidad.data?.length > 0) {
                setChartRentabilidad({
                    labels: resRentabilidad.data.map(p => p.producto?.nombre || "N/A"),
                    data: resRentabilidad.data.map(p => parseFloat(p.utilidadTotalGenerada)),
                    colors: resRentabilidad.data.map((_, i) => `rgba(75, 192, 192, 0.8)`),
                    titulo: "Rentabilidad por Producto (Utilidad)"
                });
            } else { setChartRentabilidad(null); }

            // 3. Productos Menos Vendidos (Cantidad)
            if (resMenos.data?.length > 0) {
                setChartMenosVendidos({
                    labels: resMenos.data.map(p => p.producto?.nombre || "N/A"),
                    data: resMenos.data.map(p => parseFloat(p.cantidadVendida)),
                    colors: resMenos.data.map(() => `rgba(255, 99, 132, 0.8)`),
                    titulo: "Productos con Menor Vendidos"
                });
            } else { setChartMenosVendidos(null); }

            // Métricas Resumen
            setMetricas(resResumen.data || {});

            // Stock Comparación
            if (resStock.data?.length > 0) {
                setChartStock({
                    labels: resStock.data.map(p => p.nombre || "N/A"),
                    data: resStock.data.map(p => parseInt(p.existencia)),
                    colors: resStock.data.map(p =>
                        p.existencia <= p.puntoReorden ? 'rgba(255, 99, 132, 0.8)' : 'rgba(54, 162, 235, 0.8)'
                    ),
                    titulo: "Comparación de Stock Actual"
                });
            } else {
                setChartStock(null);
            }

            // Rotación de Productos
            if (resRotacion.data?.length > 0) {
                setChartRotacion({
                    labels: resRotacion.data.map(d =>
                        `${d.producto?.nombre || 'S/N'} (${moment(d.fecha).format("DD/MM")})`
                    ),
                    datasets: [{
                        label: "Unidades Despachadas",
                        data: resRotacion.data.map(p => parseFloat(p.cantidadDespachada)),
                        borderColor: '#4e73df', 
                        backgroundColor: 'rgba(78, 115, 223, 0.1)', 
                        fill: true,
                        tension: 0.3,
                        pointRadius: 5,
                        pointBackgroundColor: '#4e73df'
                    }],
                    titulo: "Rotación de Productos"
                });
            } else {
                setChartRotacion(null);
            }

        } catch (error) {
            console.error("Error al cargar métricas:", error);
        } finally {
            setCargando(false);
        }
    }, [obtenerFechasDeFiltro]);

    useEffect(() => { cargarTodo(); }, []);

    const datos = {
        titulo: {
            titulo: "Página principal",
            url: "/app/admin/analisis/productos",
            tituloUrl: "Menu Principal",
            nombreUrl: "Inicio ",
        },
    };

    return (
        <Page datos={datos}>
            <Card>
                <FiltroFechaUI
                    filtro={filtro} setFiltro={setFiltro}
                    fechaSeleccionada={fechaSeleccionada} setFechaSeleccionada={setFechaSeleccionada}
                    horaSeleccionada={horaSeleccionada} setHoraSeleccionada={setHoraSeleccionada}
                    semanaSeleccionada={semanaSeleccionada} setSemanaSeleccionada={setSemanaSeleccionada}
                    mesSeleccionado={mesSeleccionado} setMesSeleccionado={setMesSeleccionado}
                    anioSeleccionado={anioSeleccionado} setAnioSeleccionado={setAnioSeleccionado}
                    handleApplyFilter={cargarTodo}
                    maxAnio={maxAnio}
                />
                <hr />
                {cargando ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2">Calculando estadísticas...</p>
                    </div>
                ) : (
                        <>
                            <div className="row mb-4">
                                {/* Variedad Productos */}
                                <div className="col-lg-3 col-md-6 mb-3">
                                    <div className="p-3 border-start border-primary border-4 bg-light rounded shadow-sm h-100">
                                        <h6 className="text-muted small text-uppercase fw-bold">Variedad Productos</h6>
                                        <h3 className="fw-bold mb-0">{metricas.totalProductos || 0}</h3>
                                    </div>
                                </div>

                                {/* Valor Inventario */}
                                <div className="col-lg-3 col-md-6 mb-3">
                                    <div className="p-3 border-start border-success border-4 bg-light rounded shadow-sm h-100">
                                        <h6 className="text-muted small text-uppercase fw-bold">Valor Inventario</h6>
                                        <h3 className="fw-bold mb-0 text-success">
                                            {formatoMoneda(parseFloat(metricas.valorTotalInventario || 0))}
                                        </h3>
                                    </div>
                                </div>

                                {/* Stock Bajo */}
                                <div className="col-lg-3 col-md-6 mb-3">
                                    <div className="p-3 border-start border-danger border-4 bg-light rounded shadow-sm h-100">
                                        <h6 className="text-muted small text-uppercase fw-bold">Stock Bajo</h6>
                                        <h3 className="fw-bold mb-0 text-danger">
                                            {metricas.productosStockBajo || 0}
                                        </h3>
                                    </div>
                                </div>

                                {/* Almacenes */}
                                <div className="col-lg-3 col-md-6 mb-3">
                                    <div className="p-3 border-start border-info border-4 bg-light rounded shadow-sm h-100">
                                        <h6 className="text-muted small text-uppercase fw-bold">Almacenes</h6>
                                        <h3 className="fw-bold mb-0 text-info">
                                            {metricas.numeroAlmacenes || 0}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                            {/* Primera fila: Top 3 Gráficos (4 columnas cada uno = 12 total) */}
                            <div className="row mb-4">
                                <div className="col-lg-4 col-md-6 mb-4">
                                    {chartRankingProductos && <GraficoBarra datos={chartRankingProductos} esMonetario={false} />}
                                </div>
                                <div className="col-lg-4 col-md-6 mb-4">
                                    {chartRentabilidad && <GraficoBarra datos={chartRentabilidad} esMonetario={true} />}
                                </div>
                                <div className="col-lg-4 col-md-12 mb-4">
                                    {chartMenosVendidos && <GraficoBarra datos={chartMenosVendidos} esMonetario={false} />}
                                </div>
                            </div>

                            {/* Segunda fila: Stock y Rotación (6 columnas cada uno = 12 total) */}
                            <div className="row mb-5">
                                <div className="col-lg-6 mb-4">
                                    <div className="p-3 bg-white shadow-sm rounded">
                                        {chartStock && <GraficoBarra datos={chartStock} esMonetario={false} />}
                                    </div>
                                </div>
                                <div className="col-lg-6 mb-4">
                                    <div className="p-3 bg-white shadow-sm rounded">
                                        <h6 className="fw-bold text-center mb-3">Tendencia de Rotación Diaria</h6>
                                        {chartRotacion && <GraficoLineal datos={chartRotacion} />}
                                    </div>
                                </div>
                            </div>
                    </> 
                )}
            </Card>
        </Page>
    );
}

export default ProductosAnalisis;